import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { formatDistanceToNow } from "date-fns";
import { Search, Users, Flame, TrendingUp, Clock, Download, ArrowUpDown, Calendar } from "lucide-react";
import type { Customer } from "@/types/crm";
import { LEAD_SCORES, LEAD_SOURCES, getLeadScoreConfig, getInitials, formatCurrency } from "@/types/crm";
import { AddCustomerDialog } from "@/components/admin/AddCustomerDialog";
import { exportToExcel } from "@/utils/excelExport";

export default function CustomersList() {
  const navigate = useNavigate();
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [scoreFilter, setScoreFilter] = useState<string>("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [isExporting, setIsExporting] = useState(false);
  const [sortField, setSortField] = useState<"name" | "score" | "ltv" | "followup" | "created_at">("created_at");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const ITEMS_PER_PAGE = 25;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const dataToExport = filteredCustomers.map(c => {
        const source = LEAD_SOURCES.find(s => s.key === c.lead_source)?.label || c.lead_source || "N/A";
        const score = LEAD_SCORES.find(s => s.key === c.lead_score)?.label || c.lead_score || "N/A";
        return {
          "First Name": c.first_name || "",
          "Last Name": c.last_name || "",
          "Email": c.email || "",
          "Phone": c.phone || "",
          "Lead Source": source,
          "Lead Score": score,
          "Lifetime Value ($ CAD)": c.lifetime_value || 0,
          "Created Date": c.created_at ? new Date(c.created_at).toLocaleDateString() : "",
        };
      });

      if (dataToExport.length === 0) {
        toast.info("No customers to export");
        return;
      }

      await exportToExcel(
        [{ name: "Customers", data: dataToExport }],
        `CustomersList_${new Date().toISOString().split("T")[0]}.xlsx`
      );
      toast.success("Customers list exported successfully");
    } catch (err: any) {
      toast.error("Failed to export customers: " + (err.message || "Unknown error"));
    } finally {
      setIsExporting(false);
    }
  };

  useEffect(() => { loadCustomers(); }, []);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, scoreFilter]);

  const loadCustomers = async () => {
    setIsLoading(true);
    const { data, error } = await supabase
      .from("customers")
      .select("id, first_name, last_name, email, phone, lead_score, lead_source, next_follow_up_at, lifetime_value, created_at")
      .is("deleted_at", null)
      .order("created_at", { ascending: false });

    if (error) {
      toast.error("Failed to load customers");
    } else {
      setCustomers((data as any[]) || []);
    }
    setIsLoading(false);
  };

  const filteredCustomers = customers.filter(c => {
    const matchesSearch = searchQuery === "" ||
      `${c.first_name} ${c.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.email?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.phone?.includes(searchQuery);
    const matchesScore = scoreFilter === "all" || c.lead_score === scoreFilter;
    return matchesSearch && matchesScore;
  });

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    let aVal: any;
    let bVal: any;

    if (sortField === "name") {
      aVal = `${a.first_name} ${a.last_name}`.toLowerCase();
      bVal = `${b.first_name} ${b.last_name}`.toLowerCase();
    } else if (sortField === "score") {
      // Define a custom weight or order for lead score
      const scoreWeight = { hot: 3, warm: 2, cold: 1, info: 0 };
      aVal = scoreWeight[a.lead_score as keyof typeof scoreWeight] || 0;
      bVal = scoreWeight[b.lead_score as keyof typeof scoreWeight] || 0;
    } else if (sortField === "ltv") {
      aVal = Number(a.lifetime_value) || 0;
      bVal = Number(b.lifetime_value) || 0;
    } else if (sortField === "followup") {
      aVal = a.next_follow_up_at ? new Date(a.next_follow_up_at).getTime() : 0;
      bVal = b.next_follow_up_at ? new Date(b.next_follow_up_at).getTime() : 0;
    } else { // created_at
      aVal = new Date(a.created_at).getTime();
      bVal = new Date(b.created_at).getTime();
    }

    if (aVal < bVal) return sortOrder === "asc" ? -1 : 1;
    if (aVal > bVal) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });

  const toggleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortOrder(prev => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortField(field);
      setSortOrder("desc");
    }
  };

  const hotCount = customers.filter(c => c.lead_score === "hot").length;
  const lifetimeTotal = customers.reduce((sum, c) => sum + (c.lifetime_value || 0), 0);

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
        <div>
          <h1 className="text-3xl font-heading font-bold">Customers</h1>
          <p className="text-muted-foreground">Manage your customer database</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleExport} disabled={isExporting}>
            <Download className="h-4 w-4 mr-1.5" />
            {isExporting ? "Exporting..." : "Export Excel"}
          </Button>
          <AddCustomerDialog onCreated={loadCustomers} onNavigate={(id) => navigate(`/admin/customers/${id}`)} />
        </div>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Users className="h-3 w-3" /> Total Customers</div>
          <div className="text-2xl font-bold">{customers.length}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Flame className="h-3 w-3" /> Hot Leads</div>
          <div className="text-2xl font-bold text-red-500">{hotCount}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><TrendingUp className="h-3 w-3" /> Lifetime Revenue</div>
          <div className="text-2xl font-bold text-green-600">{formatCurrency(lifetimeTotal)}</div>
        </CardContent></Card>
        <Card><CardContent className="pt-4 pb-3">
          <div className="flex items-center gap-2 text-muted-foreground text-xs mb-1"><Clock className="h-3 w-3" /> Overdue Follow-ups</div>
          <div className="text-2xl font-bold text-amber-500">
            {customers.filter(c => {
              if (!c.next_follow_up_at) return false;
              const fDate = new Date(c.next_follow_up_at);
              const thirtyDaysAgo = new Date();
              thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
              return fDate < new Date() && fDate >= thirtyDaysAgo;
            }).length}
          </div>
        </CardContent></Card>
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name, email, or phone..."
            value={searchQuery}
            onChange={e => setSearchQuery(e.target.value)}
            className="pl-10"
          />
        </div>
        <Select value={scoreFilter} onValueChange={setScoreFilter}>
          <SelectTrigger className="w-[150px]"><SelectValue placeholder="All Scores" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Scores</SelectItem>
            {LEAD_SCORES.map(s => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Customer Table */}
      {isLoading ? (
        <div className="text-center py-12 text-muted-foreground">Loading customers...</div>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-muted/50 border-b select-none">
                  <th onClick={() => toggleSort("name")} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase cursor-pointer hover:bg-muted">
                    <span className="flex items-center gap-1">
                      Customer
                      <ArrowUpDown className="h-3 w-3" />
                    </span>
                  </th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Phone</th>
                  <th className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase">Source</th>
                  <th onClick={() => toggleSort("score")} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase cursor-pointer hover:bg-muted">
                    <span className="flex items-center gap-1">
                      Score
                      <ArrowUpDown className="h-3 w-3" />
                    </span>
                  </th>
                  <th onClick={() => toggleSort("ltv")} className="text-right px-4 py-3 text-xs font-semibold text-muted-foreground uppercase cursor-pointer hover:bg-muted">
                    <span className="flex items-center justify-end gap-1">
                      LTV
                      <ArrowUpDown className="h-3 w-3" />
                    </span>
                  </th>
                  <th onClick={() => toggleSort("followup")} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase cursor-pointer hover:bg-muted">
                    <span className="flex items-center gap-1">
                      Follow-up
                      <ArrowUpDown className="h-3 w-3" />
                    </span>
                  </th>
                  <th onClick={() => toggleSort("created_at")} className="text-left px-4 py-3 text-xs font-semibold text-muted-foreground uppercase cursor-pointer hover:bg-muted">
                    <span className="flex items-center gap-1">
                      Created
                      <ArrowUpDown className="h-3 w-3" />
                    </span>
                  </th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const paginatedCustomers = sortedCustomers.slice(
                    (currentPage - 1) * ITEMS_PER_PAGE,
                    currentPage * ITEMS_PER_PAGE
                  );

                  return (
                    <>
                      {paginatedCustomers.map(customer => {
                        const sc = getLeadScoreConfig(customer.lead_score);
                        const source = LEAD_SOURCES.find(s => s.key === customer.lead_source);
                        return (
                          <tr key={customer.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => navigate(`/admin/customers/${customer.id}`)}>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-3">
                                <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-xs font-bold flex-shrink-0">
                                  {getInitials(customer.first_name, customer.last_name)}
                                </div>
                                <div>
                                  <div className="font-semibold text-sm">{customer.first_name} {customer.last_name}</div>
                                  <div className="text-xs text-muted-foreground">{customer.email || ''}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">{customer.phone || '-'}</td>
                            <td className="px-4 py-3"><Badge variant="outline" className="text-xs">{source?.label || customer.lead_source}</Badge></td>
                            <td className="px-4 py-3">
                              <Badge style={{ backgroundColor: sc.bg, color: sc.color, borderColor: sc.border }} className="text-xs border">{sc.label}</Badge>
                            </td>
                            <td className="px-4 py-3 text-sm text-right font-medium">
                              {formatCurrency(customer.lifetime_value || 0)}
                            </td>
                            <td className="px-4 py-3 text-sm">
                              {customer.next_follow_up_at ? (
                                (() => {
                                  const fDate = new Date(customer.next_follow_up_at);
                                  const thirtyDaysAgo = new Date();
                                  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
                                  const overdue = fDate < new Date() && fDate >= thirtyDaysAgo;
                                  const stale = fDate < thirtyDaysAgo;

                                  if (overdue) {
                                    return <span className="text-red-500 font-semibold">Overdue</span>;
                                  } else if (stale) {
                                    return <span className="text-muted-foreground/60">Stale</span>;
                                  } else {
                                    return <span className="text-muted-foreground">{formatDistanceToNow(fDate, { addSuffix: true })}</span>;
                                  }
                                })()
                              ) : <span className="text-muted-foreground">-</span>}
                            </td>
                            <td className="px-4 py-3 text-sm text-muted-foreground">
                              {new Date(customer.created_at).toLocaleDateString("en-CA")}
                            </td>
                            <td className="px-4 py-3 text-xs text-primary">View →</td>
                          </tr>
                        );
                      })}
                      {filteredCustomers.length === 0 && (
                        <tr><td colSpan={8} className="text-center py-8 text-muted-foreground">No customers found</td></tr>
                      )}
                    </>
                  );
                })()}
              </tbody>
            </table>
          </div>
          {(() => {
            const totalPages = Math.ceil(filteredCustomers.length / ITEMS_PER_PAGE);
            if (totalPages <= 1) return null;
            return (
              <div className="flex flex-col sm:flex-row justify-between items-center px-4 py-3 border-t gap-3 bg-muted/20">
                <div className="text-xs text-muted-foreground">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1} to {Math.min(currentPage * ITEMS_PER_PAGE, filteredCustomers.length)} of {filteredCustomers.length} entries
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setCurrentPage(p => Math.max(1, p - 1)); }}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </Button>
                  <div className="text-xs font-medium">
                    Page {currentPage} of {totalPages}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={(e) => { e.stopPropagation(); setCurrentPage(p => Math.min(totalPages, p + 1)); }}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </Button>
                </div>
              </div>
            );
          })()}
        </Card>
      )}
    </div>
  );
}
